import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { AiAutomationService } from '../../services/ai-automation.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-ai-automation',
    templateUrl: './ai-automation.component.html',
    styleUrls: ['./ai-automation.component.css'],
    standalone: true,
    imports: [CommonModule, FormsModule, TranslateModule]
})
export class AiAutomationComponent implements OnInit, OnDestroy {
    @Input() device: any;

    mode: 'document' = 'document';
    appPackage: string = '';
    appActivity: string = '';
    selectedFile: File | null = null;
    isRunning: boolean = false;

    automationResults: any[] = [];
    sessionId: string = '';

    private sub: Subscription | null = null;

    constructor(private aiService: AiAutomationService) { }

    ngOnInit(): void {
        this.sub = this.aiService.messages$.subscribe(msg => {
            this.handleSocketMessage(msg);
        });
    }

    ngOnDestroy(): void {
        if (this.sub) {
            this.sub.unsubscribe();
        }
        this.aiService.disconnect();
    }

    setMode(mode: 'document') {
        this.mode = mode;
        this.automationResults = [];
        this.sessionId = '';
    }

    onFileSelected(event: any) {
        this.selectedFile = event.target.files[0];
        this.automationResults = [];
        this.sessionId = '';
    }

    runAutomation() {
        if (!this.selectedFile || this.isRunning) return;

        this.isRunning = true;
        this.automationResults = [];
        this.sessionId = '';

        const reader = new FileReader();
        reader.onload = (e: any) => {
            const content = e.target.result;
            const steps = content.split('\n').map((l: string) => l.trim()).filter((l: string) => l.length > 0);

            if (steps.length === 0) {
                alert('The uploaded file is empty.');
                this.isRunning = false;
                return;
            }

            // Connect and send run message
            this.aiService.connect();

            // Short delay to ensure socket is open before sending
            setTimeout(() => {
                this.aiService.sendRunMessage(
                    steps, 
                    this.appPackage, 
                    this.appActivity, 
                    this.device ? this.device.id : null,
                    this.device ? this.device.serial : null,
                    'http://192.168.4.40:4723'
                    // this.device ? this.device.providerUrl : null
                );
            }, 500);
        };
        reader.readAsText(this.selectedFile);
    }

    isDownloading: boolean = false;

    async downloadScript() {
        if (!this.sessionId) return;
        this.isDownloading = true;
        try {
            const blob = await this.aiService.downloadScript(this.sessionId);
            
            // Create a link element, set its href to the blob URL, and click it to trigger download
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `replay_${this.sessionId}.py`;
            document.body.appendChild(a);
            a.click();
            
            // Clean up
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            alert('Error downloading script: ' + error.message);
        } finally {
            this.isDownloading = false;
        }
    }

    private handleSocketMessage(msg: any) {
        switch (msg.type) {
            case 'session':
                this.sessionId = msg.session_id;
                console.log('Session ID captured:', this.sessionId);
                break;

            case 'action':
                // Real-time action log
                console.log('Action received:', msg);
                this.automationResults.push(msg);
                
                // Stop the loader if we receive the FINISHED action
                if (msg.Action === 'FINISHED' || (msg.action && msg.action.toUpperCase() === 'FINISHED')) {
                    this.isRunning = false;
                }
                break;

            case 'status':
                console.log('Status update:', msg.status);
                if (['completed', 'failed', 'cancelled'].includes(msg.status)) {
                    this.isRunning = false;
                    if (msg.status === 'completed') {
                        // Optional: show completion toast
                    }
                }
                break;

            case 'error':
                console.error('Socket error message:', msg.message);
                
                // Only alert if we were still actively running (avoids alert on normal backend disconnect)
                if (this.isRunning) {
                    this.isRunning = false;
                    alert('Error: ' + msg.message);
                }
                break;

            case 'heartbeat':
                // Quietly ignore heartbeats
                break;
        }
    }
}
